<template>
  <section class="canvas-community" :style="sectionStyle" aria-labelledby="canvas-community-title">
    <a
      class="canvas-community__logo-link"
      :style="boxStyle({ x: 725, y: 200, w: 469, h: 146 })"
      href="https://canvas.storeel.com"
      aria-label="Open StoReel Canvas"
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        class="canvas-community__logo"
        :src="content.logo"
        alt="StoReel Canvas"
        loading="lazy"
        decoding="async"
        draggable="false"
      />
    </a>

    <h2
      id="canvas-community-title"
      class="canvas-community__headline"
      :style="textBoxStyle({ x: 362, y: 446, w: 1197, h: 70 })"
    >
      {{ content.title }}
    </h2>
    <p class="canvas-community__copy" :style="textBoxStyle({ x: 318, y: 566, w: 1284, h: 126 })">
      {{ content.description }}
    </p>

    <article
      v-for="card in featureCards"
      :key="card.title"
      class="canvas-community__feature-card"
      :style="imageCardStyle(card)"
    >
      <h3>{{ card.title }}</h3>
      <p>{{ card.description }}</p>
    </article>

    <a class="canvas-community__cta" :style="boxStyle({ x: 843, y: 1305, w: 234, h: 48 })" href="https://canvas.storeel.com" target="_blank" rel="noopener noreferrer">
      Start Creating
    </a>

    <h3 class="canvas-community__creation-title" :style="textBoxStyle({ x: 663, y: 1453, w: 594, h: 210 })">
      {{ content.creationTitle }}
    </h3>
    <h3
      v-if="content.systemTitle"
      class="canvas-community__system-title"
      :style="textBoxStyle({ x: 460, y: 1713, w: 1000, h: 70 })"
    >
      {{ content.systemTitle }}
    </h3>
    <p
      class="canvas-community__copy canvas-community__creation-copy"
      :style="textBoxStyle({ x: 582, y: 1713, w: 756, h: 84 })"
    >
      {{ content.creationDescription }}
    </p>

    <article
      v-for="(card, index) in valueCards"
      :key="card.title"
      :class="['canvas-community__value-card', { 'is-active': index === activeValueIndex }]"
      :style="boxStyle(card.box)"
      tabindex="0"
      @click="setActiveValue(index)"
      @focusin="setActiveValue(index)"
      @pointerenter="setActiveValue(index)"
    >
      <h4>{{ card.title }}</h4>
      <p>{{ card.description }}</p>
    </article>

    <a class="canvas-community__cta" :style="boxStyle({ x: 843, y: 2567, w: 234, h: 48 })" href="https://canvas.storeel.com" target="_blank" rel="noopener noreferrer">
      Start Creating
    </a>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { CSSProperties } from 'vue'

import type {
  CanvasFeatureCard,
  CanvasValueCard,
  DesignBox,
} from '../../data/home2026'

const props = defineProps<{
  content: {
    height: number
    logo: string
    valueHoverImage: string
    title: string
    description: string
    creationTitle: string
    systemTitle: string
    creationDescription: string
  }
  featureCards: CanvasFeatureCard[]
  valueCards: CanvasValueCard[]
  scaledPx: (value: number) => string
}>()

const activeValueIndex = ref(1)

const setActiveValue = (index: number) => {
  activeValueIndex.value = index
}

const sectionStyle = computed<CSSProperties>(() => ({
  height: props.scaledPx(props.content.height),
  '--canvas-card-radius': props.scaledPx(20),
  '--canvas-title-font': props.scaledPx(50),
  '--canvas-title-line': props.scaledPx(70),
  '--canvas-copy-font': props.scaledPx(30),
  '--canvas-copy-line': props.scaledPx(42),
  '--canvas-feature-padding-x': props.scaledPx(30),
  '--canvas-feature-padding-y': props.scaledPx(30),
  '--canvas-feature-title-font': props.scaledPx(30),
  '--canvas-feature-title-line': props.scaledPx(42),
  '--canvas-feature-copy-font': props.scaledPx(20),
  '--canvas-feature-copy-line': props.scaledPx(28),
  '--canvas-feature-copy-top': props.scaledPx(35),
  '--canvas-value-padding-x': props.scaledPx(25),
  '--canvas-value-padding-right': props.scaledPx(8),
  '--canvas-value-padding-y': props.scaledPx(50),
  '--canvas-value-title-font': props.scaledPx(36),
  '--canvas-value-title-line': props.scaledPx(50.4),
  '--canvas-value-copy-font': props.scaledPx(30),
  '--canvas-value-copy-line': props.scaledPx(42),
  '--canvas-value-copy-top': props.scaledPx(50),
  '--canvas-button-font': props.scaledPx(16),
  '--canvas-value-hover-image': `url("${props.content.valueHoverImage}")`,
}))

const boxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  height: props.scaledPx(box.h),
})

const textBoxStyle = (box: DesignBox): CSSProperties => boxStyle(box)

const imageCardStyle = (card: CanvasFeatureCard): CSSProperties =>
  ({
    ...boxStyle(card.box),
    '--card-image': `url("${card.image}")`,
  }) as CSSProperties
</script>

<style scoped>
.canvas-community {
  position: relative;
  z-index: 3;
  width: 100%;
  overflow: hidden;
  background: #000;
  color: #fff;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-synthesis: none;
  text-rendering: geometricprecision;
}

.canvas-community__logo-link,
.canvas-community__headline,
.canvas-community__copy,
.canvas-community__creation-title,
.canvas-community__system-title,
.canvas-community__feature-card,
.canvas-community__value-card,
.canvas-community__cta {
  position: absolute;
  box-sizing: border-box;
}

.canvas-community__logo-link {
  display: block;
  color: inherit;
  text-decoration: none;
}

.canvas-community__logo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.canvas-community__logo-link:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

.canvas-community__headline,
.canvas-community__creation-title,
.canvas-community__system-title,
.canvas-community__copy {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  letter-spacing: 0;
  text-align: center;
  white-space: pre-line;
}

.canvas-community__headline {
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-title-font);
  font-weight: 1000;
  line-height: var(--canvas-title-line);
}

.canvas-community__creation-title,
.canvas-community__system-title {
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-title-font);
  font-weight: 1000;
  line-height: var(--canvas-title-line);
}

.canvas-community__copy {
  color: #fff;
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-copy-font);
  font-weight: 700;
  line-height: var(--canvas-copy-line);
}

.canvas-community__creation-copy {
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 400;
}

.canvas-community__feature-card,
.canvas-community__value-card {
  overflow: hidden;
  border-radius: var(--canvas-card-radius);
}

.canvas-community__feature-card {
  color: #fff;
  text-align: left;
}

.canvas-community__value-card {
  background-color: #8abbf9;
  background-image: none;
  background-position: center;
  background-size: cover;
  color: #000;
  text-align: left;
  transition:
    background-color 180ms ease,
    color 180ms ease;
}

.canvas-community__feature-card {
  padding: var(--canvas-feature-padding-y) var(--canvas-feature-padding-x);
  background-image: var(--card-image);
  background-repeat: no-repeat;
  background-size: cover;
}

.canvas-community__feature-card::after {
  position: absolute;
  z-index: 0;
  inset: 0;
  background: rgb(0 0 0 / 18%);
  content: '';
}

.canvas-community__value-card.is-active {
  background-color: #000;
  background-image: var(--canvas-value-hover-image);
  color: #fff;
}

.canvas-community__value-card:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

.canvas-community__feature-card h3,
.canvas-community__feature-card p,
.canvas-community__value-card h4,
.canvas-community__value-card p {
  position: relative;
  z-index: 1;
  margin: 0;
  white-space: pre-line;
}

.canvas-community__feature-card h3 {
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-feature-title-font);
  font-weight: 700;
  line-height: var(--canvas-feature-title-line);
}

.canvas-community__feature-card p {
  margin-top: var(--canvas-feature-copy-top);
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-feature-copy-font);
  font-weight: 400;
  line-height: var(--canvas-feature-copy-line);
}

.canvas-community__value-card {
  padding: var(--canvas-value-padding-y) var(--canvas-value-padding-right) var(--canvas-value-padding-y)
    var(--canvas-value-padding-x);
}

.canvas-community__value-card h4 {
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-value-title-font);
  font-weight: 700;
  line-height: var(--canvas-value-title-line);
}

.canvas-community__value-card p {
  margin-top: var(--canvas-value-copy-top);
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-value-copy-font);
  font-weight: 400;
  line-height: var(--canvas-value-copy-line);
}

.canvas-community__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #fff;
  color: #000;
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--canvas-button-font);
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  text-decoration: none;
  transition:
    background 180ms ease,
    color 180ms ease,
    transform 180ms ease;
}

.canvas-community__cta:hover,
.canvas-community__cta:focus-visible {
  background: #8abbf9;
  color: #000;
  transform: translateY(-1px);
}

.canvas-community__cta:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

</style>
